import { Link } from 'react-router-dom';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl font-bold text-gray-800 mt-8 mb-3">
      {children}
    </h3>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-600 text-base leading-relaxed mb-4">
      {children}
    </p>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-lightbulb-line text-teal-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-teal-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

interface BenefitCardProps {
  icon: string;
  title: string;
  description: string;
}

function BenefitCard({ icon, title, description }: BenefitCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 flex flex-col gap-3">
      <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl">
        <i className={`${icon} text-teal-500 text-xl`}></i>
      </div>
      <h4 className="font-bold text-gray-900 text-base leading-snug">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

interface FeatureCardProps {
  number: string;
  title: string;
  description: string;
}

function FeatureCard({ number, title, description }: FeatureCardProps) {
  return (
    <div className="flex gap-5 items-start">
      <div className="shrink-0 w-10 h-10 flex items-center justify-center bg-gray-900 text-white rounded-xl font-extrabold text-sm">
        {number}
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-base mb-1">{title}</h4>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

const benefits: BenefitCardProps[] = [
  {
    icon: 'ri-palette-line',
    title: 'Unique Aesthetic Appeal',
    description: 'The flowing, organic shapes of the tent create a sense of fluidity and elegance, making them a stunning backdrop for any occasion. The natural, earthy tones blend seamlessly with outdoor environments, enhancing the overall atmosphere of your event.',
  },
  {
    icon: 'ri-layout-grid-line',
    title: 'Versatility in Event Design',
    description: 'Luxury Bedouin tents are incredibly versatile, allowing you to create a customised event space that suits your specific needs, from weddings and corporate functions to festivals and private parties.',
  },
  {
    icon: 'ri-cloud-windy-line',
    title: 'All-Weather Protection',
    description: 'These tents are designed to withstand various weather conditions. The high-quality fabric is waterproof and UV-resistant, offering protection from both rain and harsh sunlight, while the robust structure ensures stability even in windy conditions.',
  },
  {
    icon: 'ri-home-heart-line',
    title: 'Comfortable & Inviting Atmosphere',
    description: 'The soft, flowing fabric creates a warm and inviting ambiance, perfect for socialising and enjoying the occasion. Climate control solutions, lighting, and seating arrangements can all be customised for optimal guest comfort.',
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Customisable Design Elements',
    description: 'No two events are the same. Whether you need specific lighting arrangements, custom branding, or unique seating configurations, Bedouin tents can be tailored to meet your exact requirements.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Eco-Friendly Event Solution',
    description: 'Our luxury Bedouin tents offer an environmentally friendly alternative to traditional event venues. The materials are often sustainable, and the temporary nature of the structure minimises the environmental footprint of your event.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Cost-Effective Event Planning',
    description: 'Renting a luxury Bedouin tent can be more affordable than booking a traditional venue, especially when considering the flexibility and customisation options available. Allocate more of your budget to catering, entertainment, and décor.',
  },
];

const features: FeatureCardProps[] = [
  {
    number: '01',
    title: 'High-Quality Materials',
    description: 'We use premium-grade fabric that is both durable and aesthetically pleasing, designed to mimic the traditional look of Bedouin tents while providing modern benefits such as water resistance, UV protection, and breathability.',
  },
  {
    number: '02',
    title: 'Flexible Sizing and Configurations',
    description: 'These tents can be adapted to fit any event space, whether you\'re hosting a small, intimate gathering or a large, lavish affair. The modular design allows for easy expansion or reduction in size as your event plans evolve.',
  },
  {
    number: '03',
    title: 'Elegant and Functional Interior Design',
    description: 'The open floor plan allows for creative interior layouts, while the flowing fabric creates a sense of movement and grace. Our team can help you create a cohesive and elegant interior that aligns with your event\'s theme.',
  },
  {
    number: '04',
    title: 'Advanced Climate Control Options',
    description: 'Luxury Bedouin tents can be equipped with advanced climate control solutions, including heating, cooling, and ventilation systems, ensuring your guests remain comfortable regardless of the weather outside.',
  },
  {
    number: '05',
    title: 'Comprehensive Event Support',
    description: 'Our team of experienced professionals will work with you from the initial planning stages to the final execution. We offer delivery, setup, and breakdown services, allowing you to focus on enjoying your event without worrying about logistics.',
  },
];

export default function ArticleBody() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">

      {/* Hero image */}
      <div className="rounded-2xl overflow-hidden mb-10 w-full h-72 md:h-96 bg-gray-100">
        <img
          src="/assets/img/699f4309f127.webp"
          alt="Luxury Bedouin Tent for exclusive events"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Intro */}
      <Paragraph>
        When it comes to hosting exclusive events, creating the perfect ambiance is key. The setting of an event can make or break the experience, and for those who seek an unforgettable atmosphere, a luxury Bedouin tent is the ideal choice.
      </Paragraph>
      <Paragraph>
        These tents offer a unique blend of traditional elegance and modern functionality, providing an exquisite venue for any high-end occasion. In this blog, we&apos;ll explore the benefits and features of luxury Bedouin tents and why they are the perfect choice for your next event.
      </Paragraph>

      {/* The Allure */}
      <SectionHeading>The Allure of Luxury Bedouin Tents</SectionHeading>
      <Paragraph>
        Luxury Bedouin tents are inspired by the traditional tents used by the Bedouin tribes of the Arabian Peninsula. These tents were designed to withstand the harsh desert environment while providing a comfortable living space. Over time, these structures have evolved into a symbol of elegance and luxury, making them a popular choice for exclusive events worldwide.
      </Paragraph>
      <Paragraph>
        At Pop Pro Tents, our luxury Bedouin tents combine the timeless beauty of traditional Bedouin designs with modern enhancements. The result is a tent that not only offers a unique aesthetic but also provides unparalleled comfort and functionality.
      </Paragraph>

      <TipBox>
        Luxury Bedouin tents are one of the most photographed event structures on social media. Their distinctive silhouettes and warm, flowing interiors create an instantly shareable atmosphere that elevates your event&apos;s online presence.
      </TipBox>

      {/* Benefits */}
      <SectionHeading>Benefits of Choosing a Luxury Bedouin Tent for Your Event</SectionHeading>
      <Paragraph>
        A luxury Bedouin tent offers a unique blend of elegance and versatility that enhances any exclusive event. Our premium Bedouin tents stand out for their distinctive charm and practical benefits. Discover why a luxury Bedouin tent is the ideal choice for creating a memorable and sophisticated event experience.
      </Paragraph>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        {benefits.map((b) => (
          <BenefitCard key={b.title} {...b} />
        ))}
      </div>

      {/* Inline image */}
      <div className="rounded-2xl overflow-hidden my-10 w-full h-64 md:h-80 bg-gray-100">
        <img
          src="/assets/img/7856d3105108.webp"
          alt="Luxury Bedouin tent interior setup for exclusive event"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Features */}
      <SectionHeading>Features of Luxury Bedouin Tents</SectionHeading>
      <Paragraph>
        Luxury Bedouin tents combine traditional charm with modern sophistication, offering distinctive features that enhance any exclusive event. From their elegant design to advanced climate control, these tents provide both style and practicality, ensuring a memorable experience for your guests.
      </Paragraph>

      <div className="flex flex-col gap-6 mt-8 bg-gray-50 rounded-2xl p-8">
        {features.map((f) => (
          <FeatureCard key={f.number} {...f} />
        ))}
      </div>

      {/* Why Pop Pro Tents */}
      <SectionHeading>Why Choose Pop Pro Tents for Your Luxury Bedouin Tent?</SectionHeading>
      <Paragraph>
        When it comes to luxury Bedouin tents, quality and service are of utmost importance. We pride ourselves on delivering exceptional products and services to our clients. Our luxury Bedouin tents are crafted with the finest materials and designed to meet the highest standards of quality and aesthetics.
      </Paragraph>
      <Paragraph>
        We understand that every event is unique, and we are committed to providing personalised service to ensure that your event is a success. From initial consultation to final setup, our team is dedicated to making your vision a reality.
      </Paragraph>
      <Paragraph>
        Whether you&apos;re planning a wedding, corporate event, or private party, Pop Pro Tents is your go-to source for luxury Bedouin tents. With our expertise and attention to detail, you can trust that your event will be an unforgettable experience.
      </Paragraph>

      <TipBox>
        Contact Pop Pro Tents today to discuss your event requirements. Our team will work with you to design the perfect Bedouin tent setup that exceeds your expectations and leaves your guests in awe.
      </TipBox>

      {/* Conclusion */}
      <SectionHeading>Conclusion</SectionHeading>
      <Paragraph>
        A luxury Bedouin tent offers a perfect blend of traditional elegance and modern sophistication, making it an ideal choice for exclusive events. With their unique aesthetic appeal, versatility, and comfort, these tents provide an extraordinary setting for any occasion.
      </Paragraph>
      <Paragraph>
        Whether you&apos;re hosting an intimate gathering or a large celebration, our luxury Bedouin tents are designed to meet your needs and exceed your expectations. Contact Pop Pro Tents today to learn more about how we can help you create the perfect setting for your next event.
      </Paragraph>

      {/* Internal links */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/products/custom-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-tent-line"></i>
          Custom Tents
        </Link>
        <Link
          to="/blog/enhance-festival-appeal-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-article-line"></i>
          Festival Appeal Guide
        </Link>
        <Link
          to="/blog/stretch-tent-rentals-guide-tips-considerations"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Rental Guide
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line"></i>
          Inquire Now
        </Link>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        <span className="text-xs text-gray-400 mr-1 self-center">Tags:</span>
        {['Bedouin Tents', 'Exclusive Events', 'Luxury Events', 'Functions & Festivals', 'Event Planning', 'Private Parties'].map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
